import { Lock } from '../../ui/icons';
import { cn } from '../../../lib/cn';
import { signedPoints } from '../../../lib/format';
import { CARD_AT_RISK, draftFloor, draftToWin, isCardComplete, type DraftCard } from '../../../lib/picks';

type OutcomeTone = 'loss' | 'floor' | 'win';

const TONE: Record<OutcomeTone, { bg: string; label: string; value: string }> = {
  loss: { bg: 'rgba(239,65,53,0.055)', label: '#B98A86', value: '#E0463B' },
  floor: { bg: 'rgba(227,168,43,0.09)', label: '#B79454', value: '#9A6B00' },
  win: { bg: 'rgba(17,179,107,0.08)', label: '#5FB394', value: '#0A9A72' }
};

/** One scenario in the outcome strip: a short "if …" label over its signed point
 *  result, tinted by tone. The strip reads best→worst (win, floor, bust) so the
 *  player sees the payoff first, then what a missed boost costs, then the 1X2 bust. */
function Outcome({ tone, label, value, emphasis = false }: { tone: OutcomeTone; label: string; value: number; emphasis?: boolean }) {
  const c = TONE[tone];
  return (
    <div className={cn('flex flex-1 flex-col gap-[3px] rounded-[10px] px-2.5 py-[7px]')} style={{ background: c.bg }}>
      <span className="text-[8.5px] font-bold uppercase tracking-[0.06em]" style={{ color: c.label }}>
        {label}
      </span>
      <span
        className={cn('num font-extrabold leading-none tracking-[-0.03em]', emphasis ? 'text-[16px]' : 'text-[14px]')}
        style={{ color: c.value }}
      >
        {signedPoints(value)}
      </span>
    </div>
  );
}

/** Pinned bet-slip footer on the card builder. */
export function BetSlipFooter({
  card,
  frozen = false,
  pending = false,
  onLock,
  lockLabel
}: {
  card: DraftCard;
  frozen?: boolean;
  pending?: boolean;
  onLock: () => void;
  lockLabel?: string;
}) {
  const ready = isCardComplete(card) && card.outcome != null;
  const toWin = draftToWin(card);
  const floor = draftFloor(card);
  const legCount = card.legs.length;
  const hasBase = card.outcome != null;

  if (!hasBase) {
    return (
      <div className="relative shrink-0 border-t border-black/[0.07] bg-white pb-[max(20px,var(--sab))] pt-[11px] shadow-[0_-12px_24px_-18px_rgba(0,0,0,0.3)]">
        <div className="mb-[11px] flex items-center justify-between">
          <div className="flex items-baseline gap-[5px]">
            <span className="num text-[26px] font-extrabold leading-none tracking-[-0.04em] text-[#C4C4CC]">—</span>
            <span className="text-[11px] font-bold text-[#A8A8B0]">to win</span>
          </div>
          <span className="text-[11px] font-bold text-[#A8A8B0]">At risk —</span>
        </div>
        <button
          type="button"
          disabled
          className="w-full rounded-[15px] bg-[#EDEDF1] py-[13px] text-center text-[15px] font-extrabold text-[#A8A8B0]"
        >
          Pick a result first
        </button>
      </div>
    );
  }

  return (
    <div className="relative shrink-0 border-t border-black/[0.07] bg-white pb-[max(20px,var(--sab))] pt-[11px] shadow-[0_-14px_28px_-18px_rgba(0,0,0,0.32)]">
      <div className="mb-2.5 flex items-stretch gap-1">
        <Outcome tone="win" label={legCount === 0 ? "If you're right" : 'If all land'} value={toWin} emphasis={legCount > 0} />
        {legCount > 0 && <Outcome tone="floor" label={legCount === 1 ? 'If boost misses' : 'If boosts miss'} value={floor} />}
        <Outcome tone="loss" label="If winner wrong" value={CARD_AT_RISK} />
      </div>

      <button
        type="button"
        disabled={!ready || pending || frozen}
        onClick={onLock}
        className={cn(
          'tap flex w-full items-center justify-center gap-2 rounded-[15px] py-[13px] text-[15px] font-extrabold tracking-[-0.01em] transition-[transform,box-shadow,color] duration-150 ease-ios-out active:scale-[0.99]',
          ready && !frozen
            ? 'bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] text-white shadow-[0_16px_30px_-16px_rgba(11,160,120,0.7)]'
            : 'cursor-not-allowed bg-[#EDEDF1] text-[#A8A8B0]'
        )}
      >
        <Lock width={16} height={16} strokeWidth={2.4} className={ready && !frozen ? 'text-white' : 'text-[#A8A8B0]'} />
        {pending ? 'Saving…' : (lockLabel ?? (frozen ? 'Back to round' : ready ? `Lock this card · to win ${toWin}` : 'Pick a result first'))}
      </button>
    </div>
  );
}
